import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@blueprintjs/core';
import { toast } from 'sonner';
import { UPLOAD_ACCEPT, replaceOriginal, uploadProblem } from './api';
import { leadImportKeys } from './keys';

/**
 * Drop a corrected file where the draft says what is wrong with it. The new
 * file keeps the draft's source, date and cost, and opens in its place.
 */
export default function ReplaceFile({ batch, onReplaced, label }: { batch: number; onReplaced: (id: number) => void; label: string }) {
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [stage, setStage] = useState<string | null>(null);
  const replace = useMutation({
    mutationFn: (file: File) => replaceOriginal(batch, file, setStage),
    onSuccess: (r, file) => {
      toast.success(`Replaced the file with ${file.name}.`);
      queryClient.invalidateQueries({ queryKey: leadImportKeys.all });
      onReplaced(r.batch);
    },
    onError: (e) => toast.error((e as Error).message),
    onSettled: () => setStage(null),
  });
  const busy = replace.isPending;
  const pick = (files: FileList | null) => {
    const f = files?.[0];
    if (!f || busy) return;
    const problem = uploadProblem(f);
    if (problem) { toast.error(`Could not upload ${f.name}. ${problem}`); return; }
    replace.mutate(f);
  };

  return (
    <div
      className={`li-replace${over ? ' is-over' : ''}${busy ? ' is-busy' : ''}`}
      onDragOver={(e) => { if (!busy) { e.preventDefault(); setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files); }}
      aria-label={label}
      role="group"
    >
      <input ref={input} type="file" hidden accept={UPLOAD_ACCEPT} aria-label={`${label}: choose a file`}
        onChange={(e) => { pick(e.target.files); e.target.value = ''; }} />
      {busy ? (
        <span className="li-replace__busy" aria-live="polite"><Spinner size={14} />{stage ?? 'Working'}…</span>
      ) : (
        <span>
          Drop the fixed file here or{' '}
          <button type="button" className="li-btn li-btn--link" onClick={() => input.current?.click()}>choose it</button>.
          {' '}The source, date and cost stay as they are.
        </span>
      )}
    </div>
  );
}
